import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  FaCalendarAlt,
  FaCheck,
  FaEdit,
  FaEllipsisH,
  FaImage,
  FaPlus,
  FaSpinner,
  FaSync,
  FaTimes,
  FaTrash,
  FaUpload,
} from "react-icons/fa";
import { MdCategory, MdOutlineDescription } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    categoryName: "",
    description: "",
    imageUrl: "",
    sequence: 0,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);

  // API base URL
  const API_URL = `${process.env.REACT_APP_API_URL}/service-category`;
  const CLOUDINARY_URL = `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/upload`;

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/get-all`);
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (err) {
      setError("Failed to fetch categories");
      toast.error("Failed to fetch categories");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Filter categories based on search
  const filteredCategories = categories.filter(
    (category) =>
      category.categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        imageUrl: previewUrl,
      });
    }
  };

  // Upload image to Cloudinary
  const uploadToCloudinary = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);
      formData.append("cloud_name", process.env.REACT_APP_CLOUDINARY_NAME);

      const response = await axios.post(CLOUDINARY_URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.secure_url) {
        return response.data.secure_url;
      }
      throw new Error("Upload failed");
    } catch (err) {
      console.error("Cloudinary upload error:", err);
      toast.error("Failed to upload image");
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setImageFile(null);
    setFormData({
      ...formData,
      imageUrl: "",
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Open modal for adding new category
  const handleAddNew = () => {
    setFormData({
      categoryName: "",
      description: "",
      imageUrl: "",
      sequence: 0,
    });
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsEditing(false);
    setIsModalOpen(true);
  };

  // Open modal for editing category
  const handleEdit = (category) => {
    setFormData({
      categoryName: category.categoryName,
      description: category.description,
      imageUrl: category.imageUrl,
      sequence: category.sequence ?? 0,
    });
    setImageFile(null);
    setSelectedCategory(category);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setIsDeleteModalOpen(true);
  };

  // Handle form submission (Create/Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let finalImageUrl = formData.imageUrl;

      // If there's a new image file, upload it to Cloudinary
      if (imageFile) {
        finalImageUrl = await uploadToCloudinary(imageFile);
      }

      const categoryData = {
        categoryName: formData.categoryName,
        description: formData.description,
        imageUrl: finalImageUrl,
        sequence: Number(formData.sequence) || 0,
      };

      let response;
      if (isEditing) {
        // Update existing category
        response = await axios.put(
          `${API_URL}/update/${selectedCategory._id}`,
          categoryData,
        );

        if (response.data.success) {
          toast.success("Category updated successfully!");
          fetchCategories();
          setIsModalOpen(false);
          setSelectedCategory(null);
          setImageFile(null);
        }
      } else {
        // Create new category
        response = await axios.post(`${API_URL}/create`, categoryData);

        if (response.data.success) {
          toast.success("Category created successfully!");
          fetchCategories();
          setIsModalOpen(false);
          setImageFile(null);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
      console.error(err);
    }
  };

  // Handle category deletion
  const handleDelete = async () => {
    try {
      const response = await axios.delete(
        `${API_URL}/delete/${selectedCategory._id}`,
      );

      if (response.data.success) {
        toast.success("Category deleted successfully!");
        fetchCategories();
        setIsDeleteModalOpen(false);
        setSelectedCategory(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
      console.error(err);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="py-3 px-6">
        <Breadcrumb
          title1={"Categories"}
          title2={"Category Management"}
          showBtn={true}
          buttonName={"Add New"}
          onBtnClick={handleAddNew}
        />

        {/* Search Bar Skeleton */}
        <div className="mb-6 animate-pulse">
          <div className="h-12 bg-gray-200 rounded-lg max-w-md"></div>
        </div>

        {/* Horizontal Cards Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse"
            >
              <div className="flex">
                <div className="w-48 h-48 bg-gray-300 flex-shrink-0"></div>
                <div className="p-6 flex-1">
                  <div className="h-6 bg-gray-300 rounded mb-4 w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2 w-2/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="py-2 px-6 bg-gradient-to-br min-h-screen from-[#FFF7F3] to-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />

      <Breadcrumb
        title1={"Categories"}
        title2={"Category Management"}
        showBtn={true}
        buttonName={"Add New"}
        onBtnClick={handleAddNew}
      />

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-1 focus:outline-none focus:ring-primaryColor focus:border-primaryColor transition-colors"
          />
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
            <MdCategory className="h-5 w-5" />
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          )}
        </div>
        {searchTerm && (
          <p className="mt-2 text-sm text-gray-500">
            Found {filteredCategories.length} category
            {filteredCategories.length !== 1 ? "s" : ""} matching "{searchTerm}"
          </p>
        )}
      </div>

      {error ? (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex flex-col md:flex-row items-center justify-center text-red-600 gap-4">
            <p className="text-lg">{error}</p>
            <button
              onClick={fetchCategories}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
            >
              <FaSync className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="mt-12 text-center">
          <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <FaImage className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            {searchTerm
              ? "No matching categories found"
              : "No categories found"}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm
              ? "Try a different search term"
              : "Get started by creating your first service category"}
          </p>
          <button
            onClick={handleAddNew}
            className="px-6 py-3 bg-gradient-to-r from-primaryColor to-brandAccentColor text-white rounded-xl hover:from-primaryColor hover:to-brandAccentColor transition-all shadow-lg hover:shadow-xl flex items-center mx-auto"
          >
            <FaPlus className="h-5 w-5 mr-2" />
            {searchTerm ? "Create New Category" : "Add Your First Category"}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {filteredCategories.map((category) => (
            <div
              key={category._id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group"
            >
              <div className="flex flex-col lg:flex-row h-full">
                {/* Category Image - Left Side */}
                <div className="lg:w-2/5 relative overflow-hidden">
                  <div className="h-48 lg:h-full">
                    <img
                      src={category.imageUrl}
                      alt={category.categoryName}
                      className="w-auto h-36 object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/400x300?text=No+Image";
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                {/* Category Info - Right Side */}
                <div className="lg:w-3/5 p-4 flex flex-col">
                  <div className="flex-1">
                    {/* Fixed header section - both status and menu button visible */}
                    <div className="flex justify-between items-start mb-2">
                      {/* Status badge - now properly positioned */}
                      <div>
                        <span className="text-xs font-medium px-3 py-1 bg-green-100 text-green-700 rounded-full flex items-center">
                          <FaCheck className="h-3 w-3 mr-1" />
                          Active
                        </span>
                      </div>
                      <span className="ml-2 text-xs font-semibold px-2 py-1 bg-blue-100 text-primaryColor rounded-full">
                        #{category.sequence}
                      </span>

                      {/* Menu button */}
                      <div className="relative group/menu">
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <FaEllipsisH className="h-5 w-5" />
                        </button>
                        <div className="absolute right-0 top-full mt-1 w-28 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all duration-200">
                          <button
                            onClick={() => handleEdit(category)}
                            className="flex items-center w-full p-2 text-sm text-left text-gray-700 hover:bg-blue-50 hover:text-primaryColor"
                          >
                            <FaEdit className="mr-2" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(category)}
                            className="flex items-center w-full p-2 text-sm text-left text-red-600 hover:bg-red-50"
                          >
                            <FaTrash className="mr-2" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Category name */}
                    <div className="mb-2">
                      <h3 className="text-base font-bold text-gray-800 truncate group-hover:text-primaryColor transition-colors">
                        {category.categoryName}
                      </h3>
                    </div>

                    {/* Description */}
                    <div>
                      <p className="text-gray-600 text-sm line-clamp-3">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor border-b px-6 py-4 flex justify-between items-cente">
              <div className="flex items-center">
                <div className="h-10 w-10 bg-brandAccentColor rounded-lg flex items-center justify-center mr-3">
                  {isEditing ? (
                    <FaEdit className="h-5 w-5 text-primaryColor" />
                  ) : (
                    <FaPlus className="h-5 w-5 text-primaryColor" />
                  )}
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {isEditing ? "Edit Category" : "Add New Category"}
                </h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-brandAccentColor rounded-full transition-colors"
              >
                <FaTimes className="h-6 w-6 text-white" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-6">
                {/* Category Name */}
                <div>
                  <label className="text-sm font-medium  mb-2 flex items-center">
                    <MdCategory className="h-4 w-4 mr-2 text-primaryColor" />
                    Category Name *
                  </label>
                  <input
                    type="text"
                    name="categoryName"
                    value={formData.categoryName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-1 focus:outline-none focus:ring-primaryColor focus:border-primaryColor transition-colors"
                    placeholder="e.g., Hair Care, Makeup, etc."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium  mb-2 flex items-center">
                    <FaCalendarAlt className="h-4 w-4 mr-2 text-primaryColor" />
                    Display Order (Sequence)
                  </label>

                  <input
                    type="number"
                    name="sequence"
                    value={formData.sequence}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl
               focus:ring-1 focus:outline-none focus:ring-primaryColor focus:border-primaryColor"
                    placeholder="0 = default order"
                  />

                  <p className="mt-1 text-xs text-gray-500">
                    Lower number appears first. Same numbers follow creation
                    order.
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm font-medium  mb-2 flex items-center">
                    <MdOutlineDescription className="h-4 w-4 mr-2 text-primaryColor" />
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-1 focus:outline-none focus:ring-primaryColor focus:border-primaryColor transition-colors"
                    placeholder="Describe what services this category includes..."
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="text-sm font-medium  mb-2 flex items-center">
                    <FaImage className="h-4 w-4 mr-2 text-primaryColor" />
                    Category Image *
                  </label>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                  />

                  {/* Upload area */}
                  {!formData.imageUrl ? (
                    <div
                      onClick={handleUploadClick}
                      className="w-full h-64 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-primaryColor hover:bg-blue-50 transition-colors"
                    >
                      <FaUpload className="h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 font-medium mb-1">
                        Click to upload image
                      </p>
                      <p className="text-sm text-gray-500">
                        PNG, JPG, GIF up to 10MB
                      </p>
                      {uploading && (
                        <div className="mt-4 flex items-center">
                          <FaSpinner className="h-4 w-4 text-primaryColor animate-spin mr-2" />
                          <span className="text-sm text-primaryColor">
                            Uploading...
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative w-full h-64 rounded-xl border overflow-hidden group">
                        <img
                          src={formData.imageUrl}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <button
                            type="button"
                            onClick={handleUploadClick}
                            className="opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all px-4 py-2 bg-white text-gray-700 rounded-lg shadow-md hover:shadow-lg"
                          >
                            Change Image
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">
                          {imageFile ? imageFile.name : "Existing image"}
                        </span>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-4 sm:space-y-0 sm:space-x-4 mt-8 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center"
                >
                  <FaTimes className="h-5 w-5 mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-6 py-3 bg-primaryBtnColor text-white rounded-xl hover:bg-primaryBtnHoverColor transition-all shadow-lg hover:shadow-xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <FaSpinner className="h-5 w-5 mr-2 animate-spin" />
                      {isEditing ? "Updating..." : "Creating..."}
                    </>
                  ) : isEditing ? (
                    <>
                      <FaCheck className="h-5 w-5 mr-2" />
                      Update Category
                    </>
                  ) : (
                    <>
                      <FaPlus className="h-5 w-5 mr-2" />
                      Create Category
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-4">
                <FaTrash className="h-8 w-8 text-red-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
                Delete Category
              </h3>

              <p className="text-gray-600 text-center mb-4">
                Are you sure you want to delete "
                <span className="font-semibold text-gray-800">
                  {selectedCategory.categoryName}
                </span>
                "?
              </p>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <FaTimes className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                  <p className="text-sm text-red-600">
                    This action cannot be undone. All services under this
                    category will also be removed!
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center"
                >
                  <FaTimes className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all flex items-center justify-center"
                >
                  <FaTrash className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
